import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { CellLabel, getDecimalPlaces, useLang } from '@shapeable/ui';
import { Region } from '@shapeable/kelp-forest-alliance-types';
import { OCEAN_BLUE } from '../../theme';

const cls = classNames('region-card-meta');

// -------- Types -------->

// -------- Props -------->

export type RegionCardMetaProps = Classable & HasChildren & { 
  entity?: Region;
};

export const RegionCardMetaDefaultProps: Omit<RegionCardMetaProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)}
  `,
});

const IndicatorsStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    padding-top: ${theme.UNIT(2)};
    ${theme.H_DOT_BG()};
  `,
});

const SectionLabelStyles = breakpoints({
  base: css`
    font-weight: 600;
    color: ${theme.COLOR('text')};
    font-size: 1.4em;
  `,
});

const IndicatorStyles = breakpoints({
  base: css`
    display: flex;
    gap: ${theme.UNIT(2)};
  `,
});


const LabelStyles = breakpoints({
  base: css`
    font-size: 1.2em;
  `,
});

const ValueStyles = breakpoints({
  base: css`
    font-weight: 600;
    font-size: 1.3em;
    color: ${theme.COLOR(OCEAN_BLUE)};
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Indicators: styled.div`${IndicatorsStyles}`,
      SectionLabel: styled(CellLabel)`${SectionLabelStyles}`,
      Indicator: styled.div`${IndicatorStyles}`,
        Label: styled(CellLabel)`${LabelStyles}`,
        Value: styled.span`${ValueStyles}`,
};

export const RegionCardMeta: Shapeable.FC<RegionCardMetaProps> = (props) => {
  const { className, entity } = props;

  const t = useLang();

  const { indicatorResults = [] } = entity || {};

  const hasIndicatorResults = indicatorResults.length > 0;
  
  return (
    <My.Container className={cls.name(className)}>
      {
        hasIndicatorResults && (
          <My.Indicators>
            {
              indicatorResults.map((indicatorResult, index) => {
                const { indicator, numericValue } = indicatorResult;
                const { unit } = indicator || {};

                const { decimals } = getDecimalPlaces(numericValue);

                if (indicator.slug !== 'area-restored-ha') {
                  return null;
                }

                return (
                  <My.Indicator key={`${indicator.slug}-${index}`}>
                    <My.Label>{t('Total Restored:')}</My.Label>
                    <My.Value>{numericValue.toFixed(decimals)} {unit?.shorthand}</My.Value>
                  </My.Indicator>
                );
              })
            }
          </My.Indicators>
        )
      }
      {
        hasIndicatorResults && (
          <My.Indicators>
            <My.SectionLabel>{t('Pledges')}</My.SectionLabel>
           {
            indicatorResults.map((indicatorResult, index) => {
              const { indicator, value } = indicatorResult;
              const { unit } = indicator || {};
              if (indicator.slug === 'is-committed-yes-or-no') {
                return (
                  <My.Indicator key={`${indicator.slug}-${index}`}>
                    <My.Label>{t('Committed To Challenge:')}</My.Label>
                    <My.Value>{value}</My.Value>
                  </My.Indicator>
                );
              }

              if (indicator.slug === 'total-pledged-restoration-area') {
                return (
                  <My.Indicator key={`${indicator.slug}-${index}`}>
                    <My.Label>{t('Area Pledged:')}</My.Label>
                    <My.Value>{value} {unit?.shorthand}</My.Value>
                  </My.Indicator>
                );
              }

              return null;
            })
            }
          </My.Indicators>
        )
      }
    </My.Container>
  )
};

RegionCardMeta.defaultProps = RegionCardMetaDefaultProps;
RegionCardMeta.cls = cls;